import { isProduction } from './app-mode.js';

// The Dev Tools panel's HMRC sandbox controls (non-prod only): a Gov-Test-Scenario per VAT endpoint and the day the
// scoring windows end on (sandbox data is from 2017-2018). The HMRC trust-score step adds them to its hmrc/trust-score
// body; the lambda applies them only to a sandbox grant off PROD, so they never change a live score.

export const HMRC_SCENARIO_ENDPOINTS = ['obligations', 'liabilities', 'penalties', 'information'] as const;
export type HmrcScenarioEndpoint = (typeof HMRC_SCENARIO_ENDPOINTS)[number];

export interface HmrcSandboxSettings {
  govTestScenario: Partial<Record<HmrcScenarioEndpoint, string>>;
  scoringNow: string;
}

const STORAGE_KEY = 'ns-hmrc-sandbox';
// As the lambda accepts them: HMRC's scenario names, and a YYYY-MM-DD day
const SCENARIO_VALUE = /^[A-Z0-9_]{1,100}$/;
const DAY = /^\d{4}-\d{2}-\d{2}$/;

export const EMPTY_HMRC_SANDBOX: HmrcSandboxSettings = { govTestScenario: {}, scoringNow: '' };

const isEndpoint = (key: string): key is HmrcScenarioEndpoint =>
  (HMRC_SCENARIO_ENDPOINTS as readonly string[]).includes(key);

function isDay(value: string): boolean {
  if (!DAY.test(value)) return false;
  const date = new Date(`${value}T00:00:00Z`);
  return !Number.isNaN(date.getTime()) && date.toISOString().slice(0, 10) === value;
}

/** Settings read back from storage, keeping only the known endpoints and string values. */
export function parseHmrcSandbox(raw: string | null): HmrcSandboxSettings {
  try {
    const stored: unknown = JSON.parse(raw ?? 'null');
    if (typeof stored !== 'object' || stored === null) return { ...EMPTY_HMRC_SANDBOX, govTestScenario: {} };
    const { govTestScenario, scoringNow } = stored as { govTestScenario?: unknown; scoringNow?: unknown };
    const scenarios: HmrcSandboxSettings['govTestScenario'] = {};
    if (typeof govTestScenario === 'object' && govTestScenario !== null) {
      for (const [key, value] of Object.entries(govTestScenario)) {
        if (isEndpoint(key) && typeof value === 'string' && value) scenarios[key] = value;
      }
    }
    return { govTestScenario: scenarios, scoringNow: typeof scoringNow === 'string' ? scoringNow : '' };
  } catch {
    return { ...EMPTY_HMRC_SANDBOX, govTestScenario: {} };
  }
}

/**
 * The fields to add to an hmrc/trust-score body: nothing on production, and only values the lambda would accept (an
 * invalid one would get the whole request refused with a 400).
 */
export function hmrcSandboxBodyFields(
  settings: HmrcSandboxSettings,
  appMode: string | null | undefined,
): { govTestScenario?: Partial<Record<HmrcScenarioEndpoint, string>>; scoringNow?: string } {
  if (!appMode || isProduction(appMode)) return {};
  const scenarios: Partial<Record<HmrcScenarioEndpoint, string>> = {};
  for (const endpoint of HMRC_SCENARIO_ENDPOINTS) {
    const value = settings.govTestScenario[endpoint]?.trim();
    if (value && SCENARIO_VALUE.test(value)) scenarios[endpoint] = value;
  }
  const scoringNow = settings.scoringNow.trim();
  return {
    ...(Object.keys(scenarios).length > 0 ? { govTestScenario: scenarios } : {}),
    ...(scoringNow && isDay(scoringNow) ? { scoringNow } : {}),
  };
}

export function readHmrcSandbox(): HmrcSandboxSettings {
  try {
    return parseHmrcSandbox(globalThis.localStorage?.getItem(STORAGE_KEY) ?? null);
  } catch {
    return { ...EMPTY_HMRC_SANDBOX, govTestScenario: {} };
  }
}

export function saveHmrcSandbox(settings: HmrcSandboxSettings) {
  try {
    globalThis.localStorage?.setItem(STORAGE_KEY, JSON.stringify(settings));
  } catch {
    // The settings then last only as long as the page
  }
}
